import {
  Facebook,
  Twitter,
  Linkedin,
  Instagram,
  Mail,
  Phone,
  MapPin,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDarkMode } from "../context/DarkModeContext";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import logo from "/findwork.jpg";

export default function Footer() {
  const { isDark } = useDarkMode();
  const { user } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigation = (path: string) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleExternalLink = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleLanguageChange = (newLanguage: "en" | "sw") => {
    setLanguage(newLanguage);
    // Optional: Save to localStorage for persistence
    localStorage.setItem("preferred-language", newLanguage);
  };

  const currentYear = new Date().getFullYear();

  const isActivePath = (path: string) => {
    return location.pathname === path;
  };

  // Navigation links with translation keys
  const quickLinks = [
    { path: "/", key: "home" },
    { path: "/about", key: "about" },
    { path: "/packages", key: "packages" },
    { path: "/find-job", key: "findJobs" },
    { path: "/community", key: "community" },
  ];

  const resourceLinks = [
    { path: "/help-center", key: "helpCenter" },
    { path: "/faq", key: "faqs" },
    { path: "/contact-support", key: "support" },
    { path: "/privacy-policy", key: "privacyPolicy" },
    { path: "/terms-of-service", key: "termsOfService" },
  ];

  return (
    <footer
      className={`py-12 mt-auto ${
        isDark ? "bg-gray-900 text-gray-300" : "bg-gray-800 text-gray-300"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 mb-8">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-3 mb-4">
              <img
                src={logo}
                alt="FindWork"
                className="h-12 w-12 rounded-full border-2 border-sky-500"
              />
              <span className="text-2xl font-bold text-white">FindWork</span>
            </div>
            <p className="text-gray-400 mb-4 max-w-md">
              {t("brandDescription")}
            </p>
            <div className="flex items-center space-x-2 text-gray-400 mb-2">
              <MapPin size={16} />
              <span className="text-sm">{t("location")}</span>
            </div>
            <div className="flex items-center space-x-2 text-gray-400 mb-2">
              <Phone size={16} />
              <span className="text-sm">{t("phone")}</span>
            </div>
            <div className="flex items-center space-x-2 text-gray-400">
              <Mail size={16} />
              <span className="text-sm">{t("email")}</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold text-white mb-4 text-lg">
              {t("quickLinks")}
            </h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <button
                    onClick={() => handleNavigation(link.path)}
                    className={`block transition-colors duration-300 hover:text-sky-400 text-left ${
                      isActivePath(link.path)
                        ? "text-sky-400 font-medium"
                        : "text-gray-400"
                    }`}
                  >
                    {t(link.key)}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="font-semibold text-white mb-4 text-lg">
              {t("resources")}
            </h3>
            <ul className="space-y-3">
              {resourceLinks.map((link) => (
                <li key={link.path}>
                  <button
                    onClick={() => handleNavigation(link.path)}
                    className={`block transition-colors duration-300 hover:text-sky-400 text-left ${
                      isActivePath(link.path)
                        ? "text-sky-400 font-medium"
                        : "text-gray-400"
                    }`}
                  >
                    {t(link.key)}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Connect & Newsletter */}
          <div>
            <h3 className="font-semibold text-white mb-4 text-lg">
              {t("stayConnected")}
            </h3>

            {/* Social Media */}
            <div className="flex space-x-4 mb-6">
              {[
                {
                  icon: Facebook,
                  url: "https://www.facebook.com/profile.php?id=61584796086016",
                  label: "Facebook",
                },
                {
                  icon: Twitter,
                  url: "https://x.com/home",
                  label: "Twitter",
                },
                {
                  icon: Linkedin,
                  url: "http://www.linkedin.com/in/find-work-261a53393",
                  label: "LinkedIn",
                },
                {
                  icon: Instagram,
                  url: "https://www.instagram.com/findworkke/",
                  label: "Instagram",
                },
              ].map((social) => (
                <button
                  key={social.label}
                  onClick={() => handleExternalLink(social.url)}
                  className="p-2 bg-gray-700 rounded-lg hover:bg-sky-600 transition-all duration-300 transform hover:scale-110"
                  aria-label={social.label}
                >
                  <social.icon size={18} />
                </button>
              ))}
            </div>

            {/* Quick Action Buttons */}
            <div className="space-y-3">
              {!user && (
                <>
                  <button
                    onClick={() => handleNavigation("/signup")}
                    className="w-full bg-sky-600 text-white py-2 px-4 rounded-lg hover:bg-sky-700 transition-colors duration-300 text-sm font-medium"
                  >
                    {t("getStarted")}
                  </button>
                  <button
                    onClick={() => handleNavigation("/login")}
                    className="w-full border border-gray-600 text-gray-300 py-2 px-4 rounded-lg hover:border-sky-400 hover:text-sky-400 transition-colors duration-300 text-sm"
                  >
                    {t("signIn")}
                  </button>
                </>
              )}
              {user && (
                <button
                  onClick={() => handleNavigation("/dashboard")}
                  className="w-full bg-sky-600 text-white py-2 px-4 rounded-lg hover:bg-sky-700 transition-colors duration-300 text-sm font-medium"
                >
                  {t("goToDashboard")}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-700 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-gray-400 text-sm">
              <p>
                © {currentYear} FindWork. {t("copyright")} |{" "}
                {t("builtWithLove")}
              </p>
            </div>

            <div className="flex items-center space-x-6 text-sm text-gray-400">
              <button
                onClick={scrollToTop}
                className="hover:text-sky-400 transition-colors duration-300 flex items-center space-x-1"
              >
                <span>{t("backToTop")}</span>
              </button>

              {/* Language Selector */}
              <div className="flex items-center space-x-2">
                <span>🌐</span>
                <select
                  value={language}
                  onChange={(e) =>
                    handleLanguageChange(e.target.value as "en" | "sw")
                  }
                  className="bg-gray-700 border border-gray-600 rounded px-2 py-1 text-gray-300 focus:outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-400"
                >
                  <option value="en">English</option>
                  {/* <option value="sw">Kiswahili</option> */}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
